import React, { useState, useEffect } from 'react';

const HalloweenSceneOverlay = () => {
  const [bats, setBats] = useState([]);
  const [ghosts, setGhosts] = useState([]);

  useEffect(() => {
    // Create flying bats
    const batCount = 8;
    const initialBats = Array.from({ length: batCount }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: 10 + Math.random() * 30,
      duration: 15 + Math.random() * 10,
      delay: Math.random() * 5,
      size: 20 + Math.random() * 20,
    }));
    setBats(initialBats);

    // Create floating ghosts
    const ghostCount = 3;
    const initialGhosts = Array.from({ length: ghostCount }, (_, i) => ({
      id: i,
      x: 20 + (i * 30),
      y: 30 + Math.random() * 20,
      duration: 10 + Math.random() * 5,
      delay: Math.random() * 3,
    }));
    setGhosts(initialGhosts);
  }, []);

  return (
    <div className="halloween-scene-overlay">
      <style>
        {`
        .halloween-scene-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: 0;
          pointer-events: none;
          overflow: hidden;
        }

        /* Dark spooky night sky */
        .spooky-night-sky {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            to bottom,
            #0a0e1a 0%,
            #1a1625 20%,
            #2a1f3d 40%,
            #3d2952 60%,
            #4a3563 80%,
            #5d4475 100%
          );
          z-index: 0;
        }

        /* Twinkling stars */
        .stars-layer {
          position: absolute;
          width: 100%;
          height: 70%;
          top: 0;
          z-index: 1;
        }

        .star {
          position: absolute;
          width: 2px;
          height: 2px;
          background: white;
          border-radius: 50%;
          animation: twinkle ease-in-out infinite;
          box-shadow: 0 0 4px rgba(255, 255, 255, 0.8);
        }

        @keyframes twinkle {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 1; }
        }

        /* Full orange moon */
        .halloween-moon {
          position: absolute;
          top: 10%;
          right: 12%;
          width: 150px;
          height: 150px;
          background: radial-gradient(
            circle at 35% 35%,
            #ff9933 0%,
            #ff7700 40%,
            #cc5500 100%
          );
          border-radius: 50%;
          box-shadow: 
            0 0 60px rgba(255, 119, 0, 0.6),
            0 0 100px rgba(255, 119, 0, 0.3),
            inset -20px -20px 40px rgba(0, 0, 0, 0.3);
          z-index: 2;
          animation: moonGlow 4s ease-in-out infinite;
        }

        @keyframes moonGlow {
          0%, 100% { 
            box-shadow: 
              0 0 60px rgba(255, 119, 0, 0.6),
              0 0 100px rgba(255, 119, 0, 0.3),
              inset -20px -20px 40px rgba(0, 0, 0, 0.3);
          }
          50% { 
            box-shadow: 
              0 0 80px rgba(255, 119, 0, 0.8),
              0 0 120px rgba(255, 119, 0, 0.5),
              inset -20px -20px 40px rgba(0, 0, 0, 0.3);
          }
        }

        /* Fog layer */
        .fog-layer {
          position: absolute;
          bottom: 0;
          width: 100%;
          height: 50%;
          background: linear-gradient(
            to top,
            rgba(138, 43, 226, 0.15) 0%,
            rgba(75, 0, 130, 0.1) 30%,
            transparent 100%
          );
          z-index: 7;
          animation: fogMove 20s ease-in-out infinite;
        }

        @keyframes fogMove {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.6; }
        }

        /* Distant dark forest */
        .distant-forest {
          position: absolute;
          bottom: 35%;
          width: 100%;
          height: 250px;
          z-index: 2;
        }

        .distant-tree {
          position: absolute;
          bottom: 0;
          width: 0;
          height: 0;
          border-left: 25px solid transparent;
          border-right: 25px solid transparent;
          border-bottom: 120px solid rgba(10, 5, 20, 0.9);
          filter: blur(2px);
        }

        /* Mid-distance spooky trees */
        .mid-forest {
          position: absolute;
          bottom: 28%;
          width: 100%;
          height: 300px;
          z-index: 3;
        }

        .spooky-tree {
          position: absolute;
          bottom: 0;
        }

        .spooky-tree-trunk {
          width: 30px;
          height: 150px;
          background: linear-gradient(to right, #1a0f2e 0%, #0f0820 100%);
          margin: 0 auto;
          position: relative;
        }

        .spooky-tree-branches {
          position: absolute;
          top: 20px;
          left: 50%;
          transform: translateX(-50%);
          width: 100px;
          height: 120px;
        }

        .branch {
          position: absolute;
          background: #0f0820;
          border-radius: 50%;
        }

        /* Graveyard with tombstones */
        .graveyard {
          position: absolute;
          bottom: 25%;
          width: 100%;
          height: 200px;
          z-index: 4;
        }

        .tombstone {
          position: absolute;
          bottom: 0;
          width: 40px;
          height: 60px;
          background: linear-gradient(135deg, #4a4a4a 0%, #2a2a2a 100%);
          border-radius: 20px 20px 5px 5px;
          box-shadow: inset -5px -5px 10px rgba(0, 0, 0, 0.5);
        }

        .tombstone::before {
          content: 'RIP';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          color: #666;
          font-size: 10px;
          font-weight: bold;
          font-family: serif;
        }

        /* Jack-o'-lanterns */
        .pumpkin-patch {
          position: absolute;
          bottom: 20%;
          width: 100%;
          height: 150px;
          z-index: 5;
        }

        .jack-o-lantern {
          position: absolute;
          bottom: 0;
          width: 60px;
          height: 55px;
          background: radial-gradient(circle, #ff8800 0%, #ff6600 50%, #cc4400 100%);
          border-radius: 50% 50% 45% 45%;
          box-shadow: 0 0 20px rgba(255, 136, 0, 0.6);
          animation: pumpkinGlow 2s ease-in-out infinite;
        }

        .jack-o-lantern::before {
          content: '';
          position: absolute;
          top: -8px;
          left: 50%;
          transform: translateX(-50%);
          width: 12px;
          height: 12px;
          background: #228b22;
          border-radius: 0 50% 50% 50%;
        }

        .pumpkin-face {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 100%;
          height: 100%;
        }

        .pumpkin-eye {
          position: absolute;
          width: 0;
          height: 0;
          border-left: 6px solid transparent;
          border-right: 6px solid transparent;
          border-bottom: 10px solid #ffeb3b;
          top: 35%;
          filter: blur(1px);
        }

        .pumpkin-eye.left {
          left: 25%;
        }

        .pumpkin-eye.right {
          right: 25%;
        }

        .pumpkin-mouth {
          position: absolute;
          bottom: 30%;
          left: 50%;
          transform: translateX(-50%);
          width: 30px;
          height: 8px;
          background: #ffeb3b;
          border-radius: 0 0 15px 15px;
          filter: blur(1px);
        }

        @keyframes pumpkinGlow {
          0%, 100% { 
            box-shadow: 0 0 20px rgba(255, 136, 0, 0.6);
          }
          50% { 
            box-shadow: 0 0 35px rgba(255, 136, 0, 0.9);
          }
        }

        /* Foreground dead trees */
        .foreground-trees {
          position: absolute;
          bottom: 18%;
          width: 100%;
          height: 350px;
          z-index: 6;
        }

        .dead-tree {
          position: absolute;
          bottom: 0;
        }

        .dead-tree-trunk {
          width: 25px;
          height: 200px;
          background: linear-gradient(to right, #0a0510 0%, #1a0820 50%, #0a0510 100%);
          position: relative;
          opacity: 0.8;
        }

        .dead-branch {
          position: absolute;
          width: 60px;
          height: 3px;
          background: #0a0510;
          transform-origin: left center;
        }

        /* Floating ghosts */
        .ghost {
          position: absolute;
          width: 60px;
          height: 80px;
          background: radial-gradient(ellipse at center, rgba(255, 255, 255, 0.8) 0%, rgba(255, 255, 255, 0.4) 70%, transparent 100%);
          border-radius: 50% 50% 0 0;
          filter: blur(3px);
          animation: ghostFloat ease-in-out infinite;
          z-index: 8;
        }

        .ghost::before {
          content: '';
          position: absolute;
          top: 20px;
          left: 15px;
          width: 8px;
          height: 8px;
          background: rgba(0, 0, 0, 0.6);
          border-radius: 50%;
          box-shadow: 22px 0 0 rgba(0, 0, 0, 0.6);
        }

        .ghost::after {
          content: '';
          position: absolute;
          bottom: -10px;
          left: 0;
          width: 100%;
          height: 15px;
          background: 
            radial-gradient(circle at 10px 0, transparent 10px, rgba(255, 255, 255, 0.6) 10px, rgba(255, 255, 255, 0.6) 15px, transparent 15px),
            radial-gradient(circle at 30px 0, transparent 10px, rgba(255, 255, 255, 0.6) 10px, rgba(255, 255, 255, 0.6) 15px, transparent 15px),
            radial-gradient(circle at 50px 0, transparent 10px, rgba(255, 255, 255, 0.6) 10px, rgba(255, 255, 255, 0.6) 15px, transparent 15px);
          filter: blur(2px);
        }

        @keyframes ghostFloat {
          0%, 100% { transform: translateY(0) translateX(0); }
          25% { transform: translateY(-15px) translateX(10px); }
          75% { transform: translateY(-10px) translateX(-10px); }
        }

        /* Flying bats */
        .bat {
          position: absolute;
          width: 30px;
          height: 15px;
          z-index: 9;
          animation: batFly linear infinite;
        }

        .bat-body {
          position: absolute;
          width: 8px;
          height: 8px;
          background: #000;
          border-radius: 50%;
          left: 50%;
          transform: translateX(-50%);
        }

        .bat-wing {
          position: absolute;
          width: 15px;
          height: 12px;
          background: #000;
          border-radius: 50% 0 50% 0;
          top: 0;
          animation: batWingFlap 0.3s ease-in-out infinite;
        }

        .bat-wing.left {
          left: -10px;
          transform: rotate(-30deg);
        }

        .bat-wing.right {
          right: -10px;
          transform: rotate(30deg) scaleX(-1);
        }

        @keyframes batFly {
          0% { 
            left: -50px; 
            top: var(--bat-y);
          }
          100% { 
            left: calc(100% + 50px); 
            top: calc(var(--bat-y) + 50px);
          }
        }

        @keyframes batWingFlap {
          0%, 100% { transform: rotate(-30deg) scaleY(1); }
          50% { transform: rotate(-45deg) scaleY(0.8); }
        }

        /* Ground with grass */
        .spooky-ground {
          position: absolute;
          bottom: 0;
          width: 100%;
          height: 20%;
          z-index: 10;
        }

        .ground-texture {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            to bottom,
            #1a1a2e 0%,
            #16213e 50%,
            #0f1624 100%
          );
          box-shadow: 
            0 -15px 40px rgba(138, 43, 226, 0.2),
            inset 0 10px 20px rgba(0, 0, 0, 0.5);
        }
        `}
      </style>

      {/* Spooky night sky */}
      <div className="spooky-night-sky" />

      {/* Stars */}
      <div className="stars-layer">
        {Array.from({ length: 50 }, (_, i) => (
          <div
            key={i}
            className="star"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 60}%`,
              animationDuration: `${2 + Math.random() * 3}s`,
              animationDelay: `${Math.random() * 2}s`,
            }}
          />
        ))}
      </div>

      {/* Halloween moon */}
      <div className="halloween-moon" />

      {/* Distant dark forest */}
      <div className="distant-forest">
        {Array.from({ length: 20 }, (_, i) => (
          <div
            key={i}
            className="distant-tree"
            style={{
              left: `${i * 8}%`,
              transform: `translateY(${Math.random() * 20}px)`,
            }}
          />
        ))}
      </div>

      {/* Mid-distance spooky trees */}
      <div className="mid-forest">
        {[15, 35, 60, 85].map((pos, i) => (
          <div key={i} className="spooky-tree" style={{ left: `${pos}%` }}>
            <div className="spooky-tree-trunk">
              <div className="spooky-tree-branches">
                <div className="branch" style={{ width: '60px', height: '40px', top: '0', left: '-15px' }} />
                <div className="branch" style={{ width: '50px', height: '35px', top: '30px', left: '10px' }} />
                <div className="branch" style={{ width: '45px', height: '30px', top: '60px', left: '-20px' }} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Graveyard with tombstones */}
      <div className="graveyard">
        {[10, 25, 45, 70, 90].map((pos, i) => (
          <div
            key={i}
            className="tombstone"
            style={{
              left: `${pos}%`,
              transform: `rotate(${Math.random() * 10 - 5}deg)`,
            }}
          />
        ))}
      </div>

      {/* Jack-o'-lanterns */}
      <div className="pumpkin-patch">
        {[8, 30, 52, 75, 92].map((pos, i) => (
          <div
            key={i}
            className="jack-o-lantern"
            style={{
              left: `${pos}%`,
              animationDelay: `${i * 0.3}s`,
            }}
          >
            <div className="pumpkin-face">
              <div className="pumpkin-eye left" />
              <div className="pumpkin-eye right" />
              <div className="pumpkin-mouth" />
            </div>
          </div>
        ))}
      </div>

      {/* Foreground dead trees */}
      <div className="foreground-trees">
        {[5, 95].map((pos, i) => (
          <div key={i} className="dead-tree" style={{ left: `${pos}%` }}>
            <div className="dead-tree-trunk">
              <div className="dead-branch" style={{ top: '30px', left: '0', transform: 'rotate(-30deg)' }} />
              <div className="dead-branch" style={{ top: '60px', right: '0', transform: 'rotate(30deg)' }} />
              <div className="dead-branch" style={{ top: '90px', left: '0', transform: 'rotate(-45deg)' }} />
              <div className="dead-branch" style={{ top: '120px', right: '0', transform: 'rotate(45deg)' }} />
            </div>
          </div>
        ))}
      </div>

      {/* Floating ghosts */}
      {ghosts.map((ghost) => (
        <div
          key={ghost.id}
          className="ghost"
          style={{
            left: `${ghost.x}%`,
            top: `${ghost.y}%`,
            animationDuration: `${ghost.duration}s`,
            animationDelay: `${ghost.delay}s`,
          }}
        />
      ))}

      {/* Flying bats */}
      {bats.map((bat) => (
        <div
          key={bat.id}
          className="bat"
          style={{
            '--bat-y': `${bat.y}%`,
            animationDuration: `${bat.duration}s`,
            animationDelay: `${bat.delay}s`,
            width: `${bat.size}px`,
            height: `${bat.size * 0.5}px`,
          }}
        >
          <div className="bat-body" />
          <div className="bat-wing left" />
          <div className="bat-wing right" />
        </div>
      ))}

      {/* Fog layer */}
      <div className="fog-layer" />

      {/* Spooky ground */}
      <div className="spooky-ground">
        <div className="ground-texture" />
      </div>
    </div>
  );
};

export default HalloweenSceneOverlay;
